import React, { useEffect, useState } from "react";
import cn from "classnames";
import Icon from "../../../components/Icon";
import {
  dispatchNextBatch,
  getCampaignProgress,
  pauseCampaign,
  resumeCampaign,
  retryCampaignFailures,
} from "../../../api/campaigns.service";
import { CHANNELS, fmtDateTime } from "../shared";
import styles from "./Progress.module.sass";

const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES"));

const REASON_LABELS = {
  newsletter_opt_out: "Opt-out de newsletter",
  email_not_confirmed: "Email sin confirmar",
  account_deleted: "Cuenta borrada",
  account_inactive: "Cuenta inactiva",
  user_not_found: "Usuario no existe",
  no_email: "Sin email",
  device_unregistered: "App desinstalada",
  topic_fallback: "Enviado por topic (sin token: no medible)",
};

/**
 * Panel de SEGUIMIENTO del envío: progreso real desde los buckets (por canal y por
 * tanda), motivos de skip, muestras de error y acciones de control (siguiente tanda,
 * pausa/reanudar, reintentar fallidos).
 */
const Progress = ({ campaignId, status, batchSize, audienceExhausted, onAction, onNotify }) => {
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const resp = await getCampaignProgress(campaignId);
      if (resp?.data) setProgress(resp.data);
    } catch (e) {
      /* silencioso: el poll reintenta */
    }
  };

  useEffect(() => {
    load();
    if (status !== "sending") return undefined;
    const interval = setInterval(load, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, status]);

  const run = async (label, fn, successMsg) => {
    setBusy(label);
    try {
      const resp = await fn(campaignId);
      onNotify?.({ severity: "success", msg: successMsg(resp?.data) });
      onAction?.();
      load();
    } catch (e) {
      onNotify?.({ severity: "error", msg: `No se pudo: ${label}` });
    } finally {
      setBusy(null);
    }
  };

  if (!progress) return null;
  const pct =
    progress.planned > 0 ? Math.round((progress.sent / progress.planned) * 100) : 0;
  const usesBatches = (batchSize ?? 0) > 0;

  return (
    <div className={styles.progress}>
      <div className={styles.totals}>
        <div className={styles.stat}>
          <div className={styles.statValue}>{fmtNum(progress.planned)}</div>
          <div className={styles.statLabel}>Planificados</div>
        </div>
        <div className={styles.stat}>
          <div className={cn(styles.statValue, styles.green)}>{fmtNum(progress.sent)}</div>
          <div className={styles.statLabel}>Entregados</div>
        </div>
        <div className={styles.stat}>
          <div className={cn(styles.statValue, styles.red)}>{fmtNum(progress.failed)}</div>
          <div className={styles.statLabel}>Fallidos</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statValue}>{fmtNum(progress.skipped)}</div>
          <div className={styles.statLabel}>Omitidos</div>
        </div>
        <div className={styles.stat}>
          <div className={cn(styles.statValue, styles.blue)}>{fmtNum(progress.pending)}</div>
          <div className={styles.statLabel}>En cola</div>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.track}>
            <div className={styles.fill} style={{ width: `${pct}%` }} />
          </div>
          <div className={styles.pct}>
            {pct}% · {status === "sending" ? "enviando…" : status === "paused" ? "en pausa" : "terminado"}
          </div>
        </div>
      </div>

      <div className={styles.actions}>
        {status === "sending" && usesBatches && !audienceExhausted && (
          <button
            className="button-stroke button-small"
            disabled={!!busy}
            onClick={() =>
              run("tanda", dispatchNextBatch, (d) => `Tanda ${d?.batch}: ${fmtNum(d?.recipients)} encolados (quedan ${fmtNum(d?.remaining)})`)
            }
          >
            <Icon name="promotion" size="16" /> {busy === "tanda" ? "Lanzando…" : "Lanzar siguiente tanda"}
          </button>
        )}
        {status === "sending" && (
          <button
            className="button-stroke button-small"
            disabled={!!busy}
            onClick={() => run("pausar", pauseCampaign, () => "Campaña en pausa")}
          >
            ⏸ Pausar
          </button>
        )}
        {status === "paused" && (
          <button
            className="button button-small"
            disabled={!!busy}
            onClick={() => run("reanudar", resumeCampaign, () => "Campaña reanudada")}
          >
            ▶ Reanudar
          </button>
        )}
        {progress.failed > 0 && (
          <button
            className="button-stroke button-small"
            disabled={!!busy}
            onClick={() =>
              run("reintentar", retryCampaignFailures, (d) => `${fmtNum(d?.requeued)} fallidos re-encolados`)
            }
          >
            <Icon name="repeat" size="16" /> {busy === "reintentar" ? "Reintentando…" : `Reintentar ${fmtNum(progress.failed)} fallidos`}
          </button>
        )}
      </div>

      <div className={styles.channels}>
        {progress.byChannel?.map((ch) => {
          const meta = CHANNELS[ch.channel] || {};
          const chPct = ch.planned > 0 ? Math.round((ch.sent / ch.planned) * 100) : 0;
          const reasons = Object.entries(ch.reasons || {}).sort((a, b) => b[1] - a[1]);
          return (
            <div key={ch.channel} className={styles.channel}>
              <div className={styles.channelHead}>
                <span className={styles.channelIcon} style={{ backgroundColor: meta.color }}>
                  <Icon name={meta.icon || "notification"} size="16" />
                </span>
                <span className={styles.channelName}>{meta.label || ch.channel}</span>
                <span className={styles.channelPct}>{chPct}%</span>
              </div>
              <div className={styles.channelTrack}>
                <div className={styles.channelFill} style={{ width: `${chPct}%` }} />
              </div>
              <div className={styles.channelNums}>
                {fmtNum(ch.sent)} / {fmtNum(ch.planned)}
                {ch.failed > 0 && <span className={styles.red}> · {fmtNum(ch.failed)} err</span>}
                {ch.pending > 0 && <span className={styles.blue}> · {fmtNum(ch.pending)} en cola</span>}
              </div>
              {reasons.length > 0 && (
                <div className={styles.reasons}>
                  {reasons.map(([reason, count]) => (
                    <span key={reason} className={styles.reason} title={reason}>
                      {REASON_LABELS[reason] || reason}: <strong>{fmtNum(count)}</strong>
                    </span>
                  ))}
                </div>
              )}
              {ch.errorSamples?.length > 0 && (
                <details className={styles.errors}>
                  <summary>Errores ({ch.errorSamples.length} muestras)</summary>
                  {ch.errorSamples.map((e, i) => (
                    <div key={i} className={styles.errorRow}>
                      <code>{e.user}</code> — {e.error}
                    </div>
                  ))}
                </details>
              )}
            </div>
          );
        })}
      </div>

      {progress.byBatch?.length > 1 && (
        <div className={styles.batches}>
          <div className={styles.batchesTitle}>Tandas</div>
          {progress.byBatch.map((b) => (
            <div key={b.batch} className={styles.batchRow}>
              <span className={styles.batchNum}>#{b.batch}</span>
              <span className={styles.batchStat}>{fmtNum(b.planned)} plan.</span>
              <span className={cn(styles.batchStat, styles.green)}>{fmtNum(b.sent)} ok</span>
              {b.failed > 0 && <span className={cn(styles.batchStat, styles.red)}>{fmtNum(b.failed)} err</span>}
              {b.pending > 0 && <span className={cn(styles.batchStat, styles.blue)}>{fmtNum(b.pending)} cola</span>}
              <span className={styles.batchDate}>
                {b.startedAt ? fmtDateTime(b.startedAt) : "sin empezar"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Progress;
